import { Box, Button, Paper, Tab, Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from '@reach/router';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import ReactGA from 'react-ga';
import { Trans, useTranslation } from 'react-i18next';

import LoadingContainer from '../../../../../../components/LoadingContainer';
import { MainColumns } from '../../../../../../components/TableGrid';
import { useAuth } from '../../../../../../contexts/AuthContext/AuthContext';
import { api } from '../../../../../../core/api';
import { CONSTANTS } from '../../../../../../core/config/constants';
import { isTrialAccess } from '../../../../../../core/utils/access.utils';
import { BenchmarkResultsInputRequest } from '../../../../../../generated/model/benchmarkResultsInputRequest';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkState } from '../../../../Context/BenchmarkContext';
import ResultsContainer from '../ResultsContainer';
import { useResultsInputs } from '../useResultsInputs';
import AdjustmentsInput from './AdjustmentsInput';
import ResultsInput from './ResultsInput';
import TestedPartyInputs from './TestedPartyInput';

interface OwnProps {
  fetchResults(formInput: BenchmarkResultsInputRequest): void;
  isExportDisabled: boolean;
}

interface TabItem {
  name: string;
  component: React.ReactNode;
  disabled?: boolean;
}

const TabNamesEnum = {
  RESULTS: 'Results',
  TESTED_PARTY: 'Tested Party - Preview Mode',
  CALCULATIONS_ADJUSTMENTS: 'Working Capital Adjustments - Preview Mode',
};

const ResultsInputTabs: React.FC<OwnProps> = ({ fetchResults, isExportDisabled }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { benchmarkId } = useParams();
  const { dataset } = useBenchmarkState();
  const dispatchErrors = useErrorsDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isDownloadForbidden = isTrialAccess(user, dataset);

  const resultsInputs = useResultsInputs(fetchResults);

  const tabItems: TabItem[] = [
    {
      name: TabNamesEnum.RESULTS,
      component: <ResultsInput resultsInputs={resultsInputs} />,
    },
    {
      name: TabNamesEnum.TESTED_PARTY,
      component: <TestedPartyInputs benchmarkId={benchmarkId} resultsInputs={resultsInputs} />,
      disabled: !!(window as any)?.env?.DISABLE_TESTED_PARTY && !user?.showPreviewFeatures,
    },
    {
      name: TabNamesEnum.CALCULATIONS_ADJUSTMENTS,
      component: <AdjustmentsInput resultsInputs={resultsInputs} />,
      disabled:
        !resultsInputs.testedPartyFile ||
        (!!(window as any)?.env?.DISABLE_ADJUSTED_PLI && !user?.showPreviewFeatures),
    },
  ];
  const [activeTab, setActiveTab] = useState(tabItems[0].name);

  const handleChange = (event: React.ChangeEvent<{}>, tab: string) => {
    ReactGA.event({ category: 'BENCHMARK_RESULT_CONTROL', action: 'TAB_CLICK', label: tab });
    setActiveTab(tab);
  };

  const transformConfigurableColumn = (column: MainColumns): (string | undefined)[] => {
    let transformedColumns = [column.headerName];

    if (column.headerName === t('companies:columns:naceCodes')) {
      transformedColumns = ['NACE primary industry code', 'NACE secondary industry code'];
    }
    if (column.headerName === t('companies:columns:sicCodes')) {
      transformedColumns = ['SIC primary industry code', 'SIC secondary industry code'];
    }
    return transformedColumns;
  };

  const getConfigurableColumnsFromMatrix = (): string[] => {
    const columnsString = window.sessionStorage.getItem(
      CONSTANTS.COLUMNS_STORAGE.KEY_PREFIX +
        CONSTANTS.COLUMNS_STORAGE.BENCHMARKING_MATRIX_STORAGE_ID,
    );
    if (columnsString) {
      try {
        const parsed = JSON.parse(columnsString);
        return _.map(parsed.columns, (col: MainColumns) => transformConfigurableColumn(col))
          .flatMap((arrOfColumns) => arrOfColumns)
          .filter((headerName): headerName is string => !!headerName);
      } catch (error) {
        return [];
      }
    }

    return [];
  };

  const downloadReport = async () => {
    const infoSnack = enqueueSnackbar(
      <Trans i18nKey={'benchmark:benchmarkResults.resultsReportSnackbarReportText'} />,
      {
        variant: 'info',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      },
    );

    try {
      await api.benchmark.downloadReport(benchmarkId, {
        finDataYearRange: resultsInputs.yearRange,
        reportFieldIds: getConfigurableColumnsFromMatrix(),
      });
    } catch (e) {
      if (infoSnack) {
        closeSnackbar(infoSnack);
      }
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error: e },
      });
    }
  };

  if (resultsInputs.isInputsLoading) {
    return <LoadingContainer />;
  }

  return (
    <form>
      <ResultsContainer>
        <Paper>
          <Box className={styles.headerContainer}>
            <Tabs
              value={activeTab}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              className={styles.tabsContainer}
            >
              {tabItems.map((tab: TabItem) => {
                return (
                  <Tab
                    id={`full-width-tab-${tab.name}`}
                    key={tab.name}
                    value={tab.name}
                    label={t(tab.name)}
                    disabled={tab.disabled}
                  />
                );
              })}
            </Tabs>
            <div className={styles.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={downloadReport}
                disabled={isDownloadForbidden || isExportDisabled}
              >
                {t('benchmark:benchmarkResults.input.exportButton')}
              </Button>
            </div>
          </Box>
          <Box className={styles.controlsContainer}>
            {tabItems.map((tabItem: TabItem) => {
              return (
                activeTab === tabItem.name && (
                  <Box
                    role="tabpanel"
                    id={`full-width-tabpanel-${tabItem.name}`}
                    key={tabItem.name}
                    className={styles.control}
                  >
                    {tabItem.component}
                  </Box>
                )
              );
            })}
          </Box>
        </Paper>
      </ResultsContainer>
    </form>
  );
};

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgb(235,235,235)',
  },
  tabsContainer: {
    padding: 0,
    borderRight: '1px solid rgb(235 235 235)',
    '& .Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'auto',
      opacity: 0.7,
    },
  },
  buttonContainer: {
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  controlsContainer: {
    height: '320px',
  },
  control: {
    height: '100%',
  },
}));

export default ResultsInputTabs;
